import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Loader2, CheckCircle2, ShieldCheck, Lock, Upload, FileText } from "lucide-react";
import { toast } from "sonner";

const emptyForm = {
  reference_company: "",
  account_length: "",
  terms: "",
  current_balance: "",
  amount_past_due: "",
  high_credit: "",
  payment_30: "",
  payment_40: "",
  payment_60: "",
  payment_90: "",
};

export default function ReferencePortal() {
  const { token } = useParams();
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [password, setPassword] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState(null);

  useEffect(() => {
    api
      .get(`/reference/${token}`)
      .then(({ data }) => {
        setCtx(data);
        if (data.status === "completed") setDone(true);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [token]);

  const verify = async (e) => {
    e.preventDefault();
    setError("");
    setVerifying(true);
    try {
      const { data } = await api.post(`/reference/${token}/verify`, { password: password.trim() });
      setCtx((c) => ({ ...c, ...data }));
      if (data.credit_data) setForm({ ...emptyForm, ...data.credit_data });
      if (data.uploaded_file) setUploadedName(data.uploaded_file.filename);
      setUnlocked(true);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setVerifying(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await api.post(`/reference/${token}/submit`, { password: password.trim(), ...form });
      setDone(true);
      toast.success("Credit information submitted. Thank you!");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const uploadPdf = async () => {
    if (!uploadFile) { toast.error("Choose a PDF file first"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("password", password.trim());
      fd.append("file", uploadFile);
      const { data } = await api.post(`/reference/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadedName(data.filename);
      setUploadFile(null);
      toast.success("PDF uploaded. Thank you!");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally { setUploading(false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5]">
        <Loader2 className="h-6 w-6 animate-spin text-[#0F172A]" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F4F4F5] p-6">
        <div className="text-center" data-testid="ref-not-found">
          <h1 className="font-display font-extrabold text-3xl text-[#0F172A]">Link not valid</h1>
          <p className="mt-2 text-zinc-500">This credit-check link is invalid or has been removed.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5]">
      {/* Header */}
      <header className="bg-[#0F172A] text-white">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center gap-3">
          <img src="/paslogo.png" alt="Peregrine Companies" className="h-10 w-auto bg-white rounded-md px-2.5 py-1.5" />
          <div className="leading-tight">
            <div className="font-display font-extrabold text-sm tracking-tight">PEREGRINE COMPANIES</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-400">Credit Information Request</div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12">
        {done ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white border border-zinc-200 p-12 text-center" data-testid="ref-done">
            <CheckCircle2 className="h-14 w-14 text-[#10B981] mx-auto" />
            <h1 className="mt-5 font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">Thank you</h1>
            <p className="mt-2 text-zinc-500 max-w-md mx-auto">
              Your credit information for <span className="font-medium text-[#0F172A]">{ctx?.applicant_company}</span> has
              been submitted to Peregrine Air Systems. This information will be kept confidential.
            </p>
          </motion.div>
        ) : !unlocked ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto bg-white border border-zinc-200 p-8">
            <div className="flex items-center gap-2 text-[#D4AF37]"><Lock className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.25em]">Secure Access</span></div>
            <h1 className="mt-3 font-display font-extrabold text-3xl tracking-tight text-[#0F172A]">Credit reference request</h1>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed">
              <span className="font-medium text-[#0F172A]">{ctx?.applicant_company}</span> has listed your company as a
              credit / trade reference. Enter the password provided to you to complete the short form.
            </p>
            <form onSubmit={verify} className="mt-6 space-y-4" data-testid="ref-verify-form">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">Access password</label>
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="ref-password"
                  className="w-full px-4 py-3 border border-zinc-300 bg-white font-mono text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
                  placeholder="Enter password"
                />
              </div>
              {error && <p className="text-sm text-[#EF4444]" data-testid="ref-verify-error">{error}</p>}
              <button
                type="submit"
                disabled={verifying}
                data-testid="ref-verify-submit"
                className="w-full flex items-center justify-center gap-2 bg-[#0F172A] text-white py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
              >
                {verifying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
              </button>
            </form>
          </motion.div>
        ) : (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
            <p className="text-xs uppercase tracking-[0.25em] text-zinc-400 mb-3">Credit Information Request</p>
            <h1 className="font-display font-extrabold text-4xl tracking-tight text-[#0F172A]">
              Reference for {ctx?.applicant_company}
            </h1>
            <p className="mt-3 text-sm text-zinc-600 leading-relaxed max-w-prose">
              The captioned company has listed you as a credit/trade reference. Please complete the form below. Your time
              and effort are greatly appreciated, and this information will be kept confidential.
            </p>

            <div className="mt-8 bg-white border border-zinc-200 p-6 sm:p-8" data-testid="ref-upload-card">
              <div className="flex items-center gap-2 text-[#D4AF37]"><Upload className="h-4 w-4" /><span className="text-xs uppercase tracking-[0.2em]">Option 1</span></div>
              <h2 className="mt-2 font-display font-bold text-xl text-[#0F172A]">Upload your own credit form</h2>
              <p className="mt-1.5 text-sm text-zinc-600">Prefer to use your company's own PDF credit reference form? Upload it here instead of filling out the form below (PDF only, max 10MB).</p>
              {uploadedName ? (
                <div className="mt-4 flex items-center gap-3 border border-[#10B981]/40 bg-[#10B981]/5 px-4 py-3 text-sm" data-testid="ref-uploaded-file">
                  <FileText className="h-4 w-4 text-[#10B981] shrink-0" />
                  <span className="text-[#0F172A] flex-1 truncate">{uploadedName}</span>
                  <a href={`${BACKEND}/api/reference/${token}/file`} target="_blank" rel="noreferrer" data-testid="ref-view-uploaded" className="text-xs text-[#0F172A] underline underline-offset-2 shrink-0">View</a>
                  <button type="button" onClick={() => setUploadedName(null)} data-testid="ref-replace-uploaded" className="text-xs text-zinc-500 hover:text-[#0F172A] shrink-0">Replace</button>
                </div>
              ) : (
                <div className="mt-4 flex flex-col sm:flex-row gap-3">
                  <input type="file" accept="application/pdf,.pdf" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="ref-upload-file"
                    className="block w-full text-sm text-zinc-600 file:mr-3 file:py-2.5 file:px-4 file:border-0 file:bg-[#0F172A] file:text-white file:text-sm file:cursor-pointer border border-zinc-300 p-1.5" />
                  <button type="button" onClick={uploadPdf} disabled={uploading} data-testid="ref-upload-submit"
                    className="flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0F172A] px-6 py-2.5 text-sm font-medium hover:bg-[#c39f2f] transition-colors disabled:opacity-60 whitespace-nowrap">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="h-4 w-4" /> Upload PDF</>}
                  </button>
                </div>
              )}
            </div>

            <div className="mt-6 mb-2 flex items-center gap-3 text-[11px] uppercase tracking-[0.2em] text-zinc-400">
              <span className="h-px flex-1 bg-zinc-200" /> or fill out the form below <span className="h-px flex-1 bg-zinc-200" />
            </div>

            <form onSubmit={submit} className="mt-4 bg-white border border-zinc-200 p-6 sm:p-8 space-y-5" data-testid="ref-credit-form">
              <RefField label="Your company name" value={form.reference_company} onChange={set("reference_company")} testid="cf-company" />
              <RefField label="How long have they had an account with your company?" value={form.account_length} onChange={set("account_length")} testid="cf-account-length" />
              <RefField label="What are your terms?" value={form.terms} onChange={set("terms")} testid="cf-terms" />
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
                <RefField label="Current balance" value={form.current_balance} onChange={set("current_balance")} testid="cf-current-balance" />
                <RefField label="Amount past due (if any)" value={form.amount_past_due} onChange={set("amount_past_due")} testid="cf-past-due" />
                <RefField label="Recent high credit balance" value={form.high_credit} onChange={set("high_credit")} testid="cf-high-credit" />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-3">Payment history</label>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <RefCheck label="30 Days" checked={form.payment_30 === "Yes"} onChange={(e) => setForm({ ...form, payment_30: e.target.checked ? "Yes" : "" })} testid="cf-30" />
                  <RefCheck label="45 Days" checked={form.payment_40 === "Yes"} onChange={(e) => setForm({ ...form, payment_40: e.target.checked ? "Yes" : "" })} testid="cf-40" />
                  <RefCheck label="60 Days" checked={form.payment_60 === "Yes"} onChange={(e) => setForm({ ...form, payment_60: e.target.checked ? "Yes" : "" })} testid="cf-60" />
                  <RefCheck label="90 Days" checked={form.payment_90 === "Yes"} onChange={(e) => setForm({ ...form, payment_90: e.target.checked ? "Yes" : "" })} testid="cf-90" />
                </div>
              </div>

              {error && <p className="text-sm text-[#EF4444]" data-testid="ref-submit-error">{error}</p>}

              <div className="flex items-center justify-between pt-2">
                <span className="flex items-center gap-1.5 text-xs text-zinc-400"><ShieldCheck className="h-3.5 w-3.5" /> Kept confidential</span>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="ref-credit-submit"
                  className="flex items-center gap-2 bg-[#0F172A] text-white px-8 py-3.5 font-medium hover:bg-[#1E293B] transition-colors disabled:opacity-60"
                >
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit credit information"}
                </button>
              </div>
            </form>
          </motion.div>
        )}
      </main>
    </div>
  );
}

function RefField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium text-[#0F172A] mb-2">{label}</label>
      <input
        value={value}
        onChange={onChange}
        data-testid={testid}
        className="w-full px-3 py-2.5 border border-zinc-300 bg-white text-sm outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent transition-shadow"
      />
    </div>
  );
}

function RefCheck({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-2.5 border border-zinc-300 px-3 py-2.5 cursor-pointer hover:bg-zinc-50 transition-colors">
      <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="h-4 w-4 accent-[#D4AF37]" />
      <span className="text-sm text-[#0F172A]">{label}</span>
    </label>
  );
}
